import { Typography } from '@mui/material'
import React, { useEffect, useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Alert,
  AlertTitle,
  ActionButton,
  Container,
  PageContainer,
  Toast,
  PlaylistList,
  Pagination
} from '../../components'
import { useLocation } from 'react-router-dom'
import { TabbedPanel } from '../../components/search-results'
import { PlaylistFilterBar } from '../../components/playlist-landing'
import { useAlert, useFilteredQuery, usePage } from '../../hooks'
import { useSearchTabs } from '../search-results/hooks'
import { Add } from '@mui/icons-material'
import { tabs, handlers, arrayNames, filterNames, sorters } from './params'
import { routes } from '../../routes'
import { useStyles } from './style'
import { useDispatch, useSelector } from 'react-redux'
import { onboardingSlice, playlistLandingSlice, toastSlice, playlistSlice } from '../../reducers'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { contentUrl } from '../../helpers/url'
import { useEventFilters } from './hooks'
import { usePlaylistFeatureGuard } from '../../hooks/usePlaylistFeatureGuard'
import { PlaylistCard } from '../../components/general-card'
import { PlaylistProgressProvider } from '../../components/playlist-progress/context'
import { ToolkitPrivateRoute } from './../../components/toolkit-private-route'

const playlistColumns = {
  xs: 12,
  sm: 6,
  md: 6,
  lg: 4,
  xl: 4
}

const listPageSizes = {
  isDesktop: 15,
  isLargeTablet: 10,
  isSmallTablet: 10,
  isMobile: 10
}

const helixMigrationPlaylistAlert = "The playlist you're trying to access has been retired or updated. Browse this page to explore other playlists you might like or create your own by clicking 'Create Playlist'."

const PlaylistLanding = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { search } = useLocation()
  const queryParams = new URLSearchParams(search)
  const isFromHelix = queryParams.get('from') === 'helix'

  usePlaylistFeatureGuard()
  const { enqueueMsg } = useAlert()
  const results = useSelector(playlistLandingSlice.selectors.selectResults)
  const total = useSelector(playlistLandingSlice.selectors.selectTotal)
  const counts = useSelector(playlistLandingSlice.selectors.selectCounts)
  const playlistOnboarding = useSelector(onboardingSlice.selectors.selectOnboardingProfile)
  const playlistIds = useSelector(playlistSlice.selectors.selectPlaylistIds)
  const [onboardingTrackEvent, batchGetPlaylistReadTimes] = useEvent([
    onboardingSlice.eventBus.onboardingTrackEvent,
    playlistSlice.eventBus.batchGetPlaylistReadTimes
  ])

  useEffect(() => {
    if (!playlistIds.length) return
    batchGetPlaylistReadTimes.trigger({ playlistIds })
  }, [batchGetPlaylistReadTimes, playlistIds])

  useEffect(() => {
    if (playlistOnboarding && playlistOnboarding?.onboarding?.playlists?.onboarded === false) {
      onboardingTrackEvent.trigger({
        type: 'onboardedPlaylist'
      })
    }
  }, [playlistOnboarding, onboardingTrackEvent])

  const showCreateSuccessToast = useSelector(state =>
    toastSlice.selectors.selectShowToast(state, 'playlist-create-success')
  )
  const showCreateFailToast = useSelector(state =>
    toastSlice.selectors.selectShowToast(state, 'playlist-create-fail')
  )
  const showDeleteSuccessToast = useSelector(state =>
    toastSlice.selectors.selectShowToast(state, 'playlist-delete-success')
  )

  const { getEventFilters } = useEventFilters()

  const { params, filters, addFilter, clearFilters } = useFilteredQuery({
    handlers,
    arrayNames,
    filterNames
  })

  // Undefined, or an object: { status: 'liked'|'unliked'|'working', playlistId }
  const [likeChange, setLikeChange] = useState()

  const handleToggleLikeSuccess = useCallback(() => {
    const { playlistId, status } = likeChange || {}
    if (!['liked', 'unliked'].includes(status)) return
    const byMostLiked = filters.sort?.toLowerCase() === 'most liked'
    dispatch(
      playlistLandingSlice.actions.toggleLike({
        playlistId,
        status,
        byMostLiked
      })
    )
    setLikeChange()
  }, [dispatch, filters, likeChange])

  useEffect(() => {
    handleToggleLikeSuccess()
  }, [handleToggleLikeSuccess])

  const [playlistSearch, playlistToggleLike] = useEvent([
    playlistLandingSlice.eventBus.getPlaylistSearchResults,
    {
      eventName: 'playlistToggleLike',
      onSuccess: status => {
        setLikeChange(status)
      },
      onError: () => {
        enqueueMsg('Unable to toggle playlist like', 'error')
      }
    }
  ])

  const {
    pageStart,
    pageSize,
    pageCurrent,
    pageCount,
    calcPageStart
  } = usePage(params, total, listPageSizes)

  const { currentTabIndex, getTabFilter } = useSearchTabs(tabs, filters)

  useEffect(() => {
    if (!pageSize) return
    playlistSearch.trigger({
      filters: getEventFilters(filters),
      size: pageSize,
      from: pageStart - 1
    })
  }, [playlistSearch, pageSize, pageStart, filters, getEventFilters])

  useEffect(() => () => dispatch(playlistLandingSlice.actions.reset()), [
    dispatch
  ])

  const handlePlaylistLike = playlistId => {
    setLikeChange({ status: 'working', playlistId })
    playlistToggleLike.trigger({ playlistId })
  }

  const handleTabChange = index =>
    navigate(addFilter({ contenttype: getTabFilter(index) }))

  const handleCreateClick = () => navigate(routes['playlist-create'].path)

  const handleToastClose = name => dispatch(toastSlice.actions.hideToast(name))

  const handlePageChange = (evt, newPage) =>
    navigate(addFilter({ start: calcPageStart(newPage) }))

  const handleCardSelect = (item) => {
    navigate(contentUrl(item, 'playlists'))
  }

  const handleFilterApply = ({ name, value }) =>
    navigate(addFilter({ [name]: value }))

  const handleFilterClearAll = () => navigate(clearFilters())

  return (
    <>
      {showCreateSuccessToast && (
        <Toast open onClose={() => handleToastClose('playlist-create-success')}>
          <Alert severity='success'>
            <AlertTitle>The playlist was successfully created.</AlertTitle>
          </Alert>
        </Toast>
      )}
      {showCreateFailToast && (
        <Toast
          open
          message='Unable to create the playlist'
          onClose={() => handleToastClose('playlist-create-fail')}
        />
      )}
      {showDeleteSuccessToast && (
        <Toast open onClose={() => handleToastClose('playlist-delete-success')}>
          <Alert severity='success'>
            <AlertTitle>The playlist was successfully deleted.</AlertTitle>
          </Alert>
        </Toast>
      )}
      <PageContainer>
        <Container maxContentWidth={1200} inset={3}>
          {isFromHelix && <Alert severity='info' sx={{ minWidth: '100%', marginBottom: '16px' }}>{helixMigrationPlaylistAlert}</Alert>}
          <Typography variant='h4' component='h1' className={classes.title}>
            Playlists
          </Typography>
          <TabbedPanel
            labels={tabs.map(({ label }) => label)}
            onChange={handleTabChange}
            value={currentTabIndex}
            filterBar={
              <PlaylistFilterBar
                counts={counts}
                sorters={sorters}
                values={filters}
                onApply={handleFilterApply}
                onClearAll={handleFilterClearAll}
                disabled={playlistSearch.isWorking}
                classes={{ root: classes.filterBar }}
              />
            }
            actions={
              <ActionButton
                fullWidth
                variant='secondary'
                startIcon={<Add />}
                size='m'
                label='Create Playlist'
                data-test='playlist-create'
                onClick={handleCreateClick}
                className={classes.actions}
              />
            }
          />
          <PlaylistProgressProvider>
            <PlaylistList
              results={
                playlistSearch.isWorking
                  ? Array(pageSize).fill(null)
                  : results.slice(0, pageSize)
              }
              columns={playlistColumns}
              renderItem={item => (
                <PlaylistCard
                  content={item}
                  onSelect={() => handleCardSelect(item)}
                  onLike={handlePlaylistLike}
                  loadingLike={likeChange?.playlistId === item?.pk}
                  playlistItems={results}
                />
              )}
            />
          </PlaylistProgressProvider>
          <Pagination
            page={pageCurrent}
            size={pageSize}
            count={pageCount}
            disabled={playlistSearch.isWorking}
            onChange={handlePageChange}
            className={classes.pagination}
          />
        </Container>
      </PageContainer>
    </>
  )
}

const Page = () => {
  return (
    <ToolkitPrivateRoute
      featureGuard='contentHub'
      element={<PlaylistLanding />}
      allowedUserGroups={/clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/}
    />
  )
}

export {
  Page as Component
}
